import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import Logo from "@/components/Logo";
import BinderMoreDropdown from "@/components/BinderMoreDropdown";
import ThemeToggle from "@/components/ThemeToggle";
import NotificationBell from "@/components/NotificationBell";
import PageTransition from "@/components/PageTransition";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  LogOut, LayoutDashboard, Users, Package, Briefcase, Cable, ScrollText, Activity,
  FileText, ShieldCheck, AlertTriangle, ShieldAlert, KeyRound, BookLock, Rocket,
  Layout, UserCog, CreditCard, MapPin, X, ArrowLeft, MoreHorizontal,
} from "lucide-react";

// Per-section binder accent colours for the admin workspace.
const ADMIN_SECTION_ACCENT = {
  overview: "#f47a20",
  people: "#3B82F6",
  sales: "#22C55E",
  content: "#A855F7",
  operations: "#14B8A6",
  governance: "#F59E0B",
};

// ── Compact utility-rail profile popover (desktop) ──────────────────────────
// Portal + fixed positioning so the menu escapes the rail's stacking context /
// overflow and always sits above the binder navigation.
function ProfilePopover({ user, onLogout, onReturnClin, hasClinicianAccess }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, bottom: 0 });

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.round(r.right + 12),
      bottom: Math.max(8, Math.round(window.innerHeight - r.bottom)),
    });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => place();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-rail-profile-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        className="w-10 h-10 rounded-full grid place-items-center text-sm font-bold transition-transform hover:scale-105"
        style={{ background: "color-mix(in oklab, #94A3B8 20%, transparent)", color: "#94A3B8", border: "1px solid color-mix(in oklab, #94A3B8 45%, transparent)" }}
        title={user?.name}
      >
        {initial}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed w-60 fl-card p-3 z-[200]"
          style={{ left: pos.left, bottom: pos.bottom, boxShadow: "var(--fl-shadow)" }}
          data-testid="admin-rail-profile-menu"
        >
          <div className="px-1 pb-3 mb-2" style={{ borderBottom: "1px solid var(--fl-border)" }}>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--fl-muted-2)" }}>Signed in as</div>
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="fl-label mt-0.5 text-[10px]">{user?.role} · admin</div>
          </div>
          {hasClinicianAccess && (
            <button
              type="button"
              data-testid="admin-return-clinician"
              onClick={() => { setOpen(false); onReturnClin(); }}
              className="w-full flex items-center gap-2 text-xs px-2 py-2 rounded-lg mb-1 transition-colors"
              style={{ border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)", color: "var(--fl-orange)" }}
            >
              <ArrowLeft size={14} /> Clinician dashboard
            </button>
          )}
          <div className="flex items-center justify-between px-2 py-2">
            <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Theme</span>
            <ThemeToggle />
          </div>
          <button
            data-testid="admin-logout"
            onClick={async () => { setOpen(false); await onLogout(); }}
            className="w-full flex items-center gap-2 text-xs px-2 py-2 rounded-lg mt-1 hover:bg-white/5 transition-colors"
            style={{ color: "var(--fl-muted)" }}
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { setRole } = useTheme();
  const loc = useLocation();
  const nav = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const hasClinicianAccess = user?.role === "clinician";

  useEffect(() => { setRole("admin"); return () => setRole(null); }, [setRole]);
  useEffect(() => { setMoreOpen(false); }, [loc.pathname]);

  const groups = [
    { key: "overview", label: "Overview", icon: LayoutDashboard, testid: "nav-admin-group-overview", items: [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin-overview" },
        { to: "/admin/readiness", label: "Production readiness", icon: Rocket, testid: "nav-admin-readiness" },
    ]},
    { key: "people", label: "People", icon: Users, testid: "nav-admin-group-people", items: [
        { to: "/admin/staff", label: "Staff", icon: Users, testid: "nav-admin-staff" },
        { to: "/admin/users", label: "Users", icon: UserCog, testid: "nav-admin-users" },
    ]},
    { key: "sales", label: "Services & Sales", icon: Briefcase, testid: "nav-admin-group-sales", items: [
        { to: "/admin/services", label: "Services", icon: Briefcase, testid: "nav-admin-services" },
        { to: "/admin/packages", label: "Packages", icon: Package, testid: "nav-admin-packages" },
        { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "nav-admin-payments" },
    ]},
    { key: "content", label: "Content", icon: Layout, testid: "nav-admin-group-content", items: [
        { to: "/admin/templates", label: "Programme templates", icon: FileText, testid: "nav-admin-templates" },
        { to: "/admin/cms", label: "Public site CMS", icon: Layout, testid: "nav-admin-cms" },
        { to: "/admin/site-contact", label: "Contact details", icon: MapPin, testid: "nav-admin-site-contact" },
    ]},
    { key: "operations", label: "Operations", icon: Cable, testid: "nav-admin-group-operations", items: [
        { to: "/admin/integrations", label: "Integrations", icon: Cable, testid: "nav-admin-integrations" },
        { to: "/admin/diagnostics", label: "Diagnostics", icon: Activity, testid: "nav-admin-diagnostics" },
    ]},
    { key: "governance", label: "Governance", icon: ShieldCheck, testid: "nav-admin-group-governance", items: [
        { to: "/admin/audit", label: "Audit log", icon: ScrollText, testid: "nav-admin-audit" },
        { to: "/admin/alerts", label: "Security alerts", icon: ShieldAlert, testid: "nav-admin-alerts" },
        { to: "/admin/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-admin-compliance" },
        { to: "/admin/breaches", label: "Breach register", icon: AlertTriangle, testid: "nav-admin-breaches" },
        { to: "/admin/dsr", label: "DSR queue", icon: FileText, testid: "nav-admin-dsr" },
        { to: "/admin/access", label: "Access & roles", icon: KeyRound, testid: "nav-admin-access" },
        { to: "/admin/trust", label: "Trust & Data Safety", icon: BookLock, testid: "nav-admin-trust" },
    ]},
  ];

  const PRIMARY_KEYS = ["overview", "people", "sales", "content", "operations"];
  const primaryGroups = groups.filter((g) => PRIMARY_KEYS.includes(g.key));
  const moreItems = groups
    .filter((g) => !PRIMARY_KEYS.includes(g.key))
    .flatMap((g) => g.items.map((it) => ({ ...it, accent: ADMIN_SECTION_ACCENT[g.key] })));

  const activeGroup = groups.find((g) => g.items.some((it) => it.to === loc.pathname));
  const subItems = activeGroup ? activeGroup.items : [];
  const sectionAccent = activeGroup ? ADMIN_SECTION_ACCENT[activeGroup.key] : "#f47a20";

  const activeItem = groups.flatMap((g) => g.items).find((it) => it.to === loc.pathname);
  const headerTitle = activeItem?.label || activeGroup?.label || "Admin";

  const returnClin = () => nav("/clinician");

  // Mobile fixed tabs (4) + More
  const mobileFixed = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin-overview" },
    { to: "/admin/staff", label: "Staff", icon: Users, testid: "nav-admin-staff" },
    { to: "/admin/services", label: "Services", icon: Briefcase, testid: "nav-admin-services" },
    { to: "/admin/integrations", label: "Ops", icon: Cable, testid: "nav-admin-integrations" },
  ];
  const MOBILE_FIXED_PATHS = mobileFixed.map((t) => t.to);
  const moreTabActive = !MOBILE_FIXED_PATHS.includes(loc.pathname);

  return (
    <div className="min-h-screen flex fl-noise" style={{ background: "var(--fl-bg)" }}>
      {/* ── Desktop vertical binder navigation (236px) ─────────────────── */}
      <aside
        className="hidden md:flex flex-col w-[236px] shrink-0 sticky fl-vbinder"
        style={{ top: "var(--fl-banner-height, 0px)", height: "calc(100vh - var(--fl-banner-height, 0px))" }}
        data-testid="admin-vbinder"
      >
        <Link to="/admin" className="block px-4 pt-5 pb-4" data-testid="brand-home" title="Admin home">
          <Logo variant="full" size="md" />
        </Link>
        <nav
          className="flex-1 flex flex-col gap-1.5 pl-3 pr-0 pb-3"
          data-testid="admin-vbinder-nav"
          onKeyDown={(e) => {
            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
            const items = Array.from(e.currentTarget.querySelectorAll('a[href], button:not([disabled])'));
            const i = items.indexOf(document.activeElement);
            if (i === -1) return;
            e.preventDefault();
            const n = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
            items[n]?.focus();
          }}
        >
          {primaryGroups.map((g) => {
            const active = activeGroup?.key === g.key;
            return (
              <Link
                key={g.key}
                to={g.items[0].to}
                data-testid={g.testid}
                data-active={active ? "true" : "false"}
                aria-current={active ? "page" : undefined}
                className="fl-vtab"
                style={{ "--fl-section": ADMIN_SECTION_ACCENT[g.key] }}
              >
                <g.icon size={16} className="shrink-0" />
                <span className="truncate">{g.label}</span>
              </Link>
            );
          })}
          <BinderMoreDropdown vertical items={moreItems} activePath={loc.pathname} accent={ADMIN_SECTION_ACCENT.governance} accentFor={(l) => l.accent} testidPrefix="nav-admin" />
        </nav>
        <div className="flex items-center gap-1.5 px-3 py-3" style={{ borderTop: "1px solid var(--fl-border)" }} data-testid="admin-vbinder-footer">
          <NotificationBell />
          {hasClinicianAccess && (
            <button
              type="button"
              onClick={returnClin}
              data-testid="admin-return-clinician-rail"
              className="w-9 h-9 rounded-lg grid place-items-center transition-colors hover:brightness-110"
              style={{ border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)", color: "var(--fl-orange)" }}
              title="Return to your clinician dashboard"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <div className="flex-1" />
          <ProfilePopover user={user} onLogout={async () => { await logout(); nav("/"); }} onReturnClin={returnClin} hasClinicianAccess={hasClinicianAccess} />
        </div>
      </aside>

      {/* ── Right column: header + binder + content ─────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop header: compact secondary sub-tab row only. Primary nav is
            the left vertical binder; its active tab names the section. */}
        <header
          className="hidden md:block sticky z-40"
          style={{ top: "var(--fl-banner-height, 0px)", background: "var(--fl-bg)" }}
          data-testid="admin-binder-header"
        >
          {subItems.length > 1 && (
            <div className="flex items-center gap-1.5 px-6 lg:px-8 pt-4 pb-1 overflow-x-auto no-scrollbar" data-testid="admin-binder-subtabs">
              {subItems.map((s) => {
                const active = loc.pathname === s.to;
                return (
                  <Link
                    key={s.to}
                    to={s.to}
                    data-testid={`sub-${s.testid}`}
                    data-active={active ? "true" : "false"}
                    className="fl-subtab shrink-0"
                    style={{ "--fl-section": sectionAccent }}
                  >
                    {s.label}
                  </Link>
                );
              })}
            </div>
          )}
        </header>

        {/* Mobile top bar */}
        <div
          className="md:hidden fixed left-0 right-0 z-50 backdrop-blur-xl px-4 py-3 flex items-center justify-between"
          style={{ top: "var(--fl-banner-height, 0px)", background: "color-mix(in oklab, var(--fl-bg) 70%, transparent)", borderBottom: "1px solid var(--fl-border)" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Link to="/admin" className="inline-flex" data-testid="admin-mobile-brand">
              <Logo variant="wordmark" size="sm" to={null} />
            </Link>
            <span
              data-testid="admin-mobile-role-pill"
              className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ background: "rgba(148,163,184,.14)", color: "#94A3B8", border: "1px solid rgba(148,163,184,.35)" }}
            >
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasClinicianAccess && (
              <button
                type="button"
                onClick={returnClin}
                data-testid="admin-return-clinician-mobile"
                className="text-[11px] px-2 py-1.5 rounded-lg flex items-center gap-1"
                style={{ border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)", color: "var(--fl-orange)" }}
              >
                <ArrowLeft size={13} /> Clinician
              </button>
            )}
            <NotificationBell testidSuffix="mobile" />
          </div>
        </div>

        <main className="fl-section-surface flex-1 pt-16 md:pt-0 pb-24 md:pb-0 min-w-0 overflow-x-hidden" style={{ "--fl-section": sectionAccent }}>
          <PageTransition className="px-4 md:px-8 lg:px-10 py-5 md:py-7 max-w-6xl fl-page">{children}</PageTransition>
        </main>
      </div>

      {/* Mobile bottom nav — 4 fixed tabs + More */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl"
        style={{ background: "color-mix(in oklab, var(--fl-bg) 92%, transparent)", borderTop: "1px solid var(--fl-border)", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="admin-mobile-nav"
      >
        <div className="grid grid-cols-5 px-1 py-1.5">
          {mobileFixed.map((l) => {
            const Icon = l.icon;
            const active = loc.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`m-${l.testid}`}
                data-active={active ? "true" : "false"}
                className="flex flex-col items-center justify-center gap-1 py-1.5 text-[10px] rounded-lg transition-colors"
                style={{ color: active ? "var(--fl-orange)" : "var(--fl-muted-2)", background: active ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "transparent" }}
              >
                <Icon size={20} />
                <span className="truncate max-w-[64px]">{l.label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            data-testid="m-nav-admin-more"
            data-active={moreTabActive ? "true" : "false"}
            className="flex flex-col items-center justify-center gap-1 py-1.5 text-[10px] rounded-lg transition-colors"
            style={{ color: moreTabActive ? "var(--fl-orange)" : "var(--fl-muted-2)", background: moreTabActive ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "transparent" }}
          >
            <MoreHorizontal size={20} />
            <span>More</span>
          </button>
        </div>
      </nav>

      {/* Mobile "More" bottom sheet */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="md:hidden rounded-t-2xl border-0 p-0 max-h-[85vh] overflow-y-auto [&>button]:hidden"
          style={{ background: "var(--fl-bg)", borderTop: "1px solid var(--fl-border)" }}
          data-testid="admin-more-sheet"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 backdrop-blur-xl" style={{ background: "color-mix(in oklab, var(--fl-bg) 85%, transparent)", borderBottom: "1px solid var(--fl-border)" }}>
            <div className="text-sm font-semibold" style={{ color: "var(--fl-text)" }}>Admin menu</div>
            <button type="button" onClick={() => setMoreOpen(false)} data-testid="admin-more-sheet-close" style={{ color: "var(--fl-muted)" }}>
              <X size={18} />
            </button>
          </div>

          <div className="px-4 py-4 space-y-4">
            {groups.map((g) => (
              <div key={g.key}>
                <div className="fl-label text-[10px] px-1 mb-2 flex items-center gap-1.5" style={{ color: "var(--fl-muted-2)" }}>
                  <g.icon size={12} /> {g.label}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {g.items.map((l) => {
                    const Icon = l.icon;
                    const active = loc.pathname === l.to;
                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setMoreOpen(false)}
                        data-testid={`more-${l.testid}`}
                        className="flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl text-[11px] text-center min-h-[72px] transition-colors"
                        style={{ color: active ? "var(--fl-orange)" : "var(--fl-text)", background: active ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "color-mix(in oklab, var(--fl-text) 5%, transparent)", border: "1px solid var(--fl-border)" }}
                      >
                        <Icon size={20} className={active ? "text-[color:var(--fl-orange)]" : ""} />
                        <span className="leading-tight">{l.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="px-5 py-4 space-y-3" style={{ borderTop: "1px solid var(--fl-border)" }}>
            {hasClinicianAccess && (
              <button
                type="button"
                data-testid="admin-return-clinician-sheet"
                onClick={() => { setMoreOpen(false); returnClin(); }}
                className="flex items-center justify-center gap-2 text-sm px-3 py-2.5 rounded-xl w-full font-medium"
                style={{ border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)", color: "var(--fl-orange)" }}
              >
                <ArrowLeft size={16} /> Clinician dashboard
              </button>
            )}
            <div className="flex items-center justify-between">
              <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Theme</div>
              <ThemeToggle />
            </div>
            <div className="pt-1">
              <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Signed in as</div>
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="fl-label mt-1">{user?.role} · admin</div>
            </div>
            <button
              data-testid="admin-logout-mobile"
              onClick={async () => { setMoreOpen(false); await logout(); nav("/"); }}
              className="flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl w-full"
              style={{ border: "1px solid var(--fl-border)", color: "var(--fl-muted)" }}
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
